import { useState, useEffect } from 'react';
import {
    FireExtinguisher, Calendar, MapPin, Save, X,
    AlertTriangle, CheckCircle, Hash, Building2, Layers
} from 'lucide-react';

interface EquipmentFormProps {
    initialData?: any;
    onClose: () => void;
    onSave: () => void;
}

interface Facility {
    id: string;
    name: string;
}

interface Zone {
    id: string;
    zoneName: string;
}

export default function EquipmentForm({ initialData, onClose, onSave }: EquipmentFormProps) {
    const [facilities, setFacilities] = useState<Facility[]>([]);
    const [zones, setZones] = useState<Zone[]>([]);
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        facilityId: initialData?.facilityId || '',
        zoneId: initialData?.zoneId || '',
        equipmentNumber: initialData?.equipmentNumber || '',
        equipmentType: initialData?.equipmentType || 'WATER',
        capacity: initialData?.capacity || '',
        status: initialData?.status || 'ACTIVE',
        locationDescription: initialData?.locationDescription || '',
        installationDate: initialData?.installationDate
            ? new Date(initialData.installationDate).toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0],
        manufactureDate: initialData?.manufactureDate // We generally don't have this in DB schema yet but UI asks for it? Schema doesn't have it.
            ? new Date(initialData.manufactureDate).toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0],
    });

    useEffect(() => {
        fetchFacilities();
        if (initialData?.facilityId) {
            fetchZones(initialData.facilityId);
        }
    }, [initialData]);

    const fetchFacilities = async () => {
        try {
            const res = await fetch('https://fire-safety-api.tedguy280.workers.dev/api/facilities', {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const data = await res.json() as { success: boolean; facilities: Facility[] };
            if (data.success) setFacilities(data.facilities);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchZones = async (facilityId: string) => {
        try {
            const res = await fetch(`https://fire-safety-api.tedguy280.workers.dev/api/facilities/${facilityId}/zones`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const data = await res.json() as { success: boolean; zones: Zone[] };
            if (data.success) setZones(data.zones);
        } catch (err) {
            console.error(err);
        }
    };

    const handleFacilityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const facilityId = e.target.value;
        setFormData({ ...formData, facilityId, zoneId: '' });
        if (facilityId) fetchZones(facilityId);
        else setZones([]);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            const url = initialData
                ? `https://fire-safety-api.tedguy280.workers.dev/api/equipment/${initialData.id}`
                : `https://fire-safety-api.tedguy280.workers.dev/api/facilities/${formData.facilityId}/equipment`;

            const method = initialData ? 'PUT' : 'POST';

            // Prepare payload - convert dates to timestamps
            const payload = {
                ...formData,
                installationDate: new Date(formData.installationDate).getTime(),
                manufactureDate: new Date(formData.manufactureDate).getTime(),
            };

            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(payload)
            });

            const data = await res.json() as { success: boolean };
            if (data.success) {
                onSave();
            }
        } catch (error) {
            console.error('Failed to save equipment:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="card-glass max-w-2xl w-full p-8 animate-scaleIn max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-start mb-6">
                    <h2 className="text-2xl font-display font-bold text-white flex items-center gap-3">
                        <FireExtinguisher className="w-8 h-8 text-ember-500" />
                        {initialData ? 'Edit Equipment' : 'Add New Equipment'}
                    </h2>
                    <button onClick={onClose} className="text-steel-400 hover:text-white transition-colors">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Location Section */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="label flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-steel-400" /> Facility
                            </label>
                            <select
                                required
                                disabled={!!initialData} // Lock facility on edit for simplicity
                                className="input"
                                value={formData.facilityId}
                                onChange={handleFacilityChange}
                            >
                                <option value="">Select Facility</option>
                                {facilities.map(f => (
                                    <option key={f.id} value={f.id}>{f.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="label flex items-center gap-2">
                                <Layers className="w-4 h-4 text-steel-400" /> Zone (Optional)
                            </label>
                            <select
                                className="input"
                                value={formData.zoneId}
                                onChange={e => setFormData({ ...formData, zoneId: e.target.value })}
                                disabled={!formData.facilityId}
                            >
                                <option value="">No Specific Zone</option>
                                {zones.map(z => (
                                    <option key={z.id} value={z.id}>{z.zoneName}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Equipment Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="label flex items-center gap-2">
                                <Hash className="w-4 h-4 text-steel-400" /> Equipment Number
                            </label>
                            <input
                                type="text"
                                required
                                className="input"
                                value={formData.equipmentNumber}
                                onChange={e => setFormData({ ...formData, equipmentNumber: e.target.value })}
                                placeholder="e.g. EXT-001"
                            />
                        </div>
                        <div>
                            <label className="label">Status</label>
                            <select
                                className="input"
                                value={formData.status}
                                onChange={e => setFormData({ ...formData, status: e.target.value })}
                            >
                                <option value="ACTIVE">Active</option>
                                <option value="MAINTENANCE">Maintenance</option>
                                <option value="RETIRED">Retired</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="label">Type</label>
                            <select
                                required
                                className="input"
                                value={formData.equipmentType}
                                onChange={e => setFormData({ ...formData, equipmentType: e.target.value })}
                            >
                                <option value="WATER">Water</option>
                                <option value="FOAM">Foam</option>
                                <option value="ABC_POWDER">ABC Powder</option>
                                <option value="BC_POWDER">BC Powder</option>
                                <option value="CO2">CO2</option>
                                <option value="WET_CHEMICAL">Wet Chemical</option>
                                <option value="CLASS_D">Class D</option>
                            </select>
                        </div>
                        <div>
                            <label className="label">Capacity</label>
                            <input
                                type="text"
                                required
                                className="input"
                                value={formData.capacity}
                                onChange={e => setFormData({ ...formData, capacity: e.target.value })}
                                placeholder="e.g. 6L, 2kg"
                            />
                        </div>
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="label flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-steel-400" /> Manufacture Date
                            </label>
                            <input
                                type="date"
                                required
                                className="input"
                                value={formData.manufactureDate}
                                onChange={e => setFormData({ ...formData, manufactureDate: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="label flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-steel-400" /> Installation Date
                            </label>
                            <input
                                type="date"
                                required
                                className="input"
                                value={formData.installationDate}
                                onChange={e => setFormData({ ...formData, installationDate: e.target.value })}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="label flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-steel-400" /> Location Description
                        </label>
                        <input
                            type="text"
                            className="input"
                            value={formData.locationDescription}
                            onChange={e => setFormData({ ...formData, locationDescription: e.target.value })}
                            placeholder="e.g. Near main entrance, beside elevator"
                        />
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-steel-700/30">
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn btn-secondary"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="btn btn-primary flex items-center gap-2"
                        >
                            {loading ? <div className="spinner w-4 h-4 border-2" /> : <Save className="w-4 h-4" />}
                            {initialData ? 'Update Equipment' : 'Save Equipment'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
